/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState, useSyncExternalStore, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useTenantContext } from "./useTenantContext";
import { isSupabasePermissionError, isSupabaseSchemaError } from "@/lib/supabase-compat";
import { getTenantSlugFromHost } from "@/lib/tenant-domain";
import { mergeChmsOrgSettings, parseChmsOrgSettings } from "@/lib/chms-settings";
import { getOrgSettingsSaveCoordinator } from "@/lib/org-settings-save";
import { PromiseTimeoutError, withPromiseTimeout } from "@/lib/promise-timeout";

const _proc =
  typeof process !== "undefined"
    ? process.env
    : ({} as Record<string, string | undefined>);

const DEFAULT_ORG_SLUG = (
  import.meta.env.VITE_DEFAULT_ORG_SLUG ||
  _proc.VITE_DEFAULT_ORG_SLUG ||
  ""
)
  .trim()
  .toLowerCase();

interface Organization {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  accent_color: string | null;
  footer_text: string | null;
  statement_header_text: string | null;
  statement_footer_text: string | null;
  legal_name: string | null;
  ein: string | null;
  settings: Record<string, unknown>;
  setup_complete?: boolean | null;
  status?: string | null;
  ccli_license_number?: string | null;
}

export function resolveTenantOrganizationSelection(
  orgs: Organization[],
  storedOrgId: string | null,
  hostname: string,
  preferredOrgSlug: string | null = null,
): string | null {
  const tenantSlug = getTenantSlugFromHost(hostname);
  if (!tenantSlug) {
    if (!storedOrgId && preferredOrgSlug) {
      const preferredOrg = orgs.find((org) => org.slug === preferredOrgSlug);
      if (preferredOrg) {
        return preferredOrg.id;
      }
    }
    return storedOrgId;
  }

  const tenantOrg = orgs.find((org) => org.slug === tenantSlug);
  if (tenantOrg) {
    return tenantOrg.id;
  }

  // On tenant hosts, never fall back to a stored org from another tenant.
  return null;
}

interface OrganizationMember {
  id: string;
  organization_id: string;
  user_id: string;
  role: "owner" | "admin" | "member";
  organization: Organization;
}

interface OrganizationContextType {
  organization: Organization | null;
  organizations: Organization[];
  organizationId: string | null;
  /** The signed-in user's id, or null when nobody is signed in. For state that must belong to one person (#6069). */
  userId: string | null;
  organizationRole: OrganizationMember["role"] | null;
  isOrgAdmin: boolean;
  isLoading: boolean;
  /**
   * True while a signed-in user's organization is still being chosen: memberships are loading, or
   * they have loaded but no organization is selected yet. Consumers that decide access must wait
   * on it, since `organizationId` is null during this window for a user who does have an
   * organization (#7231). False outside the provider, where there is nothing to wait for.
   */
  isResolving: boolean;
  error: string | null;
  needsOnboarding: boolean;
  switchOrganization: (orgId: string) => void;
  refetch: () => Promise<void>;
  updateSettingsOptimistically: (
    newSettings: Record<string, unknown>,
    organizationId?: string | null
  ) => void;
  /**
   * Cancels an organization refetch that is already in flight. Await it before an optimistic
   * write: a refetch that started before the write commits would otherwise land afterwards
   * and revert the optimistic value (#7765).
   */
  cancelOrganizationRefetch: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

const ACTIVE_ORG_KEY = "forge_active_org";

/**
 * The church the user is working in right now, read from the persisted selection instead of a
 * render. For a callback that outlives its component (a mutation finishing after a switch
 * unmounted it), where a captured organization id is the one from when it was created.
 */
export function readActiveOrganizationId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_ORG_KEY);
  } catch {
    return null;
  }
}

// The church on screen, support mode included, as of the provider's latest render.
let effectiveOrganizationId: string | null = null;

/**
 * The church on screen right now, including a support-mode override that never touches the
 * persisted selection. Like `readActiveOrganizationId`, for a callback that outlives its
 * component; falls back to the persisted selection before the provider has rendered.
 */
export function readEffectiveOrganizationId(): string | null {
  return effectiveOrganizationId ?? readActiveOrganizationId();
}

// Shape of a single row returned from the organization_members select. The
// upstream supabase-js typings are loose because we narrow from two different
// select shapes (FULL vs COMPAT), so we model the superset here.
interface MembershipRow {
  id: string;
  organization_id: string;
  user_id: string;
  role: OrganizationMember["role"];
  organization: Organization | null;
}

// Sentinel value returned from the queryFn when the underlying table is
// inaccessible (missing schema / RLS denial). We can't throw, because throwing
// would set query.error and surface as a user-facing error; instead we tag the
// result and let the component translate it into the `orgBootstrapUnavailable`
// flag that the rest of the app already understands.
type MembershipsResult =
  | { kind: "ok"; memberships: MembershipRow[] }
  | { kind: "unavailable" };

const MEMBERSHIPS_QUERY_KEY = "organization-memberships";
export const ORGANIZATION_BOOTSTRAP_TIMEOUT_MS = 15_000;

async function fetchMembershipsForUser(userId: string): Promise<MembershipsResult> {
  // Fetch organizations the user belongs to.
  // Try with setup_complete first; fall back without it if the column doesn't exist yet
  // (migration 20260617000004 may not have been applied to all environments).
  const ORG_FIELDS_FULL = `
      id, organization_id, user_id, role,
      organization:organizations (
        id, name, slug, logo_url, accent_color,
        footer_text, statement_header_text, statement_footer_text,
        legal_name, ein,
        settings, setup_complete, status, ccli_license_number
      )`;
  const ORG_FIELDS_COMPAT = `
      id, organization_id, user_id, role,
      organization:organizations (
        id, name, slug, logo_url, accent_color,
        footer_text, statement_header_text, statement_footer_text,
        legal_name, ein,
        settings, status, ccli_license_number
      )`;

  let result = await supabase
    .from("organization_members")
    .select(ORG_FIELDS_FULL)
    .eq("user_id", userId)
    .order("created_at", { ascending: true })
    .order("organization_id", { ascending: true });

  // If the query failed (likely due to missing setup_complete column), retry without it
  if (result.error) {
    result = await supabase
      .from("organization_members")
      .select(ORG_FIELDS_COMPAT)
      .eq("user_id", userId)
      .order("created_at", { ascending: true })
      .order("organization_id", { ascending: true });
  }

  if (result.error) {
    if (isSupabaseSchemaError(result.error) || isSupabasePermissionError(result.error)) {
      console.warn("organization_members unavailable; skipping organization bootstrap");
      return { kind: "unavailable" };
    }
    throw result.error;
  }

  return { kind: "ok", memberships: (result.data ?? []) as unknown as MembershipRow[] };
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const { tenantContext, setBaseContext } = useTenantContext();
  const queryClient = useQueryClient();

  const [activeOrgId, setActiveOrgId] = useState<string | null>(() => {
    // Try to restore from localStorage
    if (typeof window !== "undefined") {
      return localStorage.getItem(ACTIVE_ORG_KEY);
    }
    return null;
  });
  const [orgBootstrapUnavailable, setOrgBootstrapUnavailable] = useState(false);

  const userId = user?.id ?? null;
  const membershipsQueryKey = [MEMBERSHIPS_QUERY_KEY, userId] as const;
  const settingsSaves = getOrgSettingsSaveCoordinator(queryClient, userId);
  const pendingSettings = useSyncExternalStore(settingsSaves.subscribe, settingsSaves.getSnapshot, settingsSaves.getSnapshot);

  const query = useQuery<MembershipsResult, Error>({
    queryKey: membershipsQueryKey,
    queryFn: () => {
      const request = fetchMembershipsForUser(userId as string);
      const cached = queryClient.getQueryData<MembershipsResult>(membershipsQueryKey);
      const hasUsableCachedOrganization =
        cached?.kind === "ok" && cached.memberships.some((membership) => Boolean(membership.organization));
      // Bound the first load so a protected route cannot spin forever, but let
      // background refreshes finish normally when a usable organization is
      // already cached. Empty cached membership results still need a deadline.
      return hasUsableCachedOrganization
        ? request
        : withPromiseTimeout(
            request,
            ORGANIZATION_BOOTSTRAP_TIMEOUT_MS,
            "Organization bootstrap timed out",
          );
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof PromiseTimeoutError) return false;
      return failureCount < 3;
    },
  });

  // Translate the queryFn's "unavailable" sentinel + schema/permission errors
  // into the existing orgBootstrapUnavailable flag, and keep it in sync.
  useEffect(() => {
    if (!userId) {
      if (orgBootstrapUnavailable) {
        setOrgBootstrapUnavailable(false);
      }
      return;
    }
    if (query.data?.kind === "unavailable") {
      if (!orgBootstrapUnavailable) {
        setOrgBootstrapUnavailable(true);
      }
      localStorage.removeItem(ACTIVE_ORG_KEY);
      if (activeOrgId !== null) {
        setActiveOrgId(null);
      }
      return;
    }
    // For any non-unavailable state (ok data or a non-schema error), make sure
    // the flag is cleared.
    if (orgBootstrapUnavailable) {
      setOrgBootstrapUnavailable(false);
    }
  }, [userId, query.data, query.error, orgBootstrapUnavailable, activeOrgId]);

  // Derive organizations + role lookup from the cached query data. This used
  // to live inside the fetch callback; moving it to useMemo means it runs
  // synchronously on cache hits (no setState round-trip) and multiple
  // consumers share the same derived values.
  const memberships = useMemo(
    () => (query.data?.kind === "ok" ? query.data.memberships.map((membership) => membership.organization ? {
      ...membership,
      organization: { ...membership.organization, settings: settingsSaves.overlay(membership.organization.id, membership.organization.settings, pendingSettings) },
    } : membership) : []),
    [query.data, settingsSaves, pendingSettings],
  );

  const organizations = useMemo<Organization[]>(
    () =>
      (memberships || [])
        .map((m) => m.organization)
        .filter((o): o is Organization => Boolean(o)),
    [memberships],
  );

  const orgRoles = useMemo<Record<string, OrganizationMember["role"]>>(
    () =>
      (memberships || []).reduce<Record<string, OrganizationMember["role"]>>(
        (acc, membership) => {
          if (membership.organization_id) {
            acc[membership.organization_id] = membership.role;
          }
          return acc;
        },
        {},
      ),
    [memberships],
  );

  // When memberships change, reconcile activeOrgId with tenant host + stored
  // value + availability. This mirrors the old fetch callback's tail logic.
  useEffect(() => {
    if (!userId) {
      return;
    }
    if (query.data?.kind !== "ok") {
      return;
    }

    if (organizations.length > 0) {
      const storedOrgId = localStorage.getItem(ACTIVE_ORG_KEY);
      const isStoredOrgValid = organizations.some((org) => org.id === storedOrgId);

      const activeOrgFromTenant = resolveTenantOrganizationSelection(
        organizations,
        storedOrgId,
        typeof window !== "undefined" ? window.location.hostname : "",
      );

      if (activeOrgFromTenant && activeOrgFromTenant !== storedOrgId) {
        if (activeOrgId !== activeOrgFromTenant) {
          setActiveOrgId(activeOrgFromTenant);
        }
        localStorage.setItem(ACTIVE_ORG_KEY, activeOrgFromTenant);
        return;
      }

      if (!storedOrgId || !isStoredOrgValid) {
        // Default to first organization
        if (activeOrgId !== organizations[0].id) {
          setActiveOrgId(organizations[0].id);
        }
        localStorage.setItem(ACTIVE_ORG_KEY, organizations[0].id);
      } else if (activeOrgId !== storedOrgId) {
        setActiveOrgId(storedOrgId);
      }
    } else {
      if (activeOrgId !== null) {
        setActiveOrgId(null);
      }
      localStorage.removeItem(ACTIVE_ORG_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- activeOrgId is intentionally omitted to avoid feedback loops; it's read fresh each run.
  }, [userId, query.data, organizations]);

  // Logout cleanup: when user becomes null, clear the active-org selection
  // from both state and localStorage. useQuery's enabled:false already stops
  // fetching, but the persisted localStorage value must be wiped explicitly
  // so the next signed-in user doesn't inherit a stale tenant.
  // A page load also starts with no user while auth restores the session. That is not a sign-out:
  // clearing then threw away the remembered organization, and access settled on "no membership"
  // before it was chosen again, so org owners opening an admin page directly got /403 (#7231).
  useEffect(() => {
    if (!userId && !authLoading) {
      if (activeOrgId !== null) {
        setActiveOrgId(null);
      }
      localStorage.removeItem(ACTIVE_ORG_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to user id and auth-settled transitions.
  }, [userId, authLoading]);

  const switchOrganization = (orgId: string) => {
    const org = organizations.find((o) => o.id === orgId);
    if (org) {
      setActiveOrgId(orgId);
      localStorage.setItem(ACTIVE_ORG_KEY, orgId);
      // Keep the base tenant context in the same state transition as the
      // explicit selection. Otherwise its previous organization wins the
      // resolver below and pins every tenant-scoped consumer until reload.
      setBaseContext({
        organizationId: org.id,
        organizationName: org.name,
        supportSessionId: null,
        source: "organization",
      });
    }
  };

  const resolvedOrganizationId = tenantContext?.organizationId ?? activeOrgId;
  // A layout effect, not a passive one: a save that finishes between the commit that renders the
  // new church and its passive effects would otherwise still read the old church (#7868).
  useLayoutEffect(() => {
    effectiveOrganizationId = resolvedOrganizationId ?? null;
  }, [resolvedOrganizationId]);

  const organizationForContext =
    organizations.find((org) => org.id === resolvedOrganizationId) ||
    organizations.find((org) => org.id === activeOrgId) ||
    null;

  // Optimistic settings update — write through to the query cache so all
  // consumers see the new value immediately and the change survives the next
  // read from cache. Falls back to a no-op if there's nothing cached yet.
  const updateSettingsOptimistically = (
    newSettings: Record<string, unknown>,
    organizationIdOverride?: string | null,
  ) => {
    const targetOrgId = organizationIdOverride ?? resolvedOrganizationId;
    if (!targetOrgId) return;
    queryClient.setQueryData<MembershipsResult>(
      [MEMBERSHIPS_QUERY_KEY, userId],
      (current) => {
        if (!current || current.kind !== "ok") return current;
        return {
          kind: "ok",
          memberships: current.memberships.map((m) => {
            if (!m?.organization || m.organization.id !== targetOrgId) return m;
            return {
              ...m,
              organization: {
                ...m.organization,
                settings: mergeChmsOrgSettings(m.organization.settings, newSettings),
              },
            };
          }),
        };
      },
    );
  };

  const refetch = async () => {
    await query.refetch();
  };

  const cancelOrganizationRefetch = async () => {
    await queryClient.cancelQueries({ queryKey: membershipsQueryKey, exact: true });
  };

  const organizationRole = resolvedOrganizationId ? orgRoles[resolvedOrganizationId] ?? null : null;
  const isOrgAdmin = organizationRole === "owner" || organizationRole === "admin";

  useEffect(() => {
      if (organizationForContext) {
        setBaseContext({
          organizationId: organizationForContext.id,
          organizationName: organizationForContext.name,
          supportSessionId: null,
          source: "organization",
        });
      } else {
        setBaseContext(null);
      }
  }, [organizationForContext, setBaseContext]);

  // Surface a user-facing error string only for real failures — schema /
  // permission errors are already funneled into orgBootstrapUnavailable and
  // should not show as errors.
  const hasUsableOrganization = organizations.length > 0;
  const error =
    query.error &&
    !hasUsableOrganization &&
    !isSupabaseSchemaError(query.error) &&
    !isSupabasePermissionError(query.error)
      ? query.error.message || "Failed to fetch organizations"
      : null;

  // Match legacy semantics: isLoading is only true while we're actively
  // fetching on behalf of a signed-in user. When the query is disabled
  // (no user), we expose false so guards don't spin forever on the login page.
  const isLoading = !!userId && query.isPending;
  // Memberships can be loaded while the selection effect above has not run yet.
  const isResolving = isLoading || (!!userId && organizations.length > 0 && !resolvedOrganizationId);

  // User needs onboarding ONLY when creating a new org (i.e., they have no organizations).
  // The onboarding state is used elsewhere for showing in-app starter tasks and setup wizards,
  // but should NOT redirect to /onboarding page if the user already has an organization.
  // CRITICAL: Only trigger if we've actually attempted to fetch (avoid race condition)
  // CRITICAL: Never redirect to onboarding if the org fetch errored — empty orgs due to
  // a query failure (e.g. missing column) is NOT the same as genuinely having zero orgs.
  const needsOnboarding =
    !isLoading &&
    !query.isFetching &&
    !query.error &&
    !error &&
    !orgBootstrapUnavailable &&
    !!user &&
    !authLoading &&
    organizations.length === 0;

  return (
    <OrganizationContext.Provider
      value={{
        organization: organizationForContext,
        organizations,
        organizationId: resolvedOrganizationId,
        userId,
        organizationRole,
        isOrgAdmin,
        isLoading,
        isResolving,
        error,
        needsOnboarding,
        switchOrganization,
        refetch,
        updateSettingsOptimistically,
        cancelOrganizationRefetch,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

/**
 * Safe loading stub returned when useOrganization is called outside of
 * OrganizationProvider.  Every value is neutral / empty so that downstream
 * components see an "is loading" state and render a loader or bail out
 * gracefully rather than crash-looping.
 */
const ORGANIZATION_LOADING_STUB: OrganizationContextType = {
  organization: null,
  organizations: [],
  organizationId: null,
  userId: null,
  organizationRole: null,
  isOrgAdmin: false,
  isLoading: true,
  isResolving: false,
  error: null,
  needsOnboarding: false,
  switchOrganization: () => {},
  refetch: async () => {},
  updateSettingsOptimistically: () => {},
  cancelOrganizationRefetch: async () => {},
};

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    // Return a safe loading stub instead of throwing.
    // This prevents crash loops when the hook is accidentally called outside
    // OrganizationProvider (e.g. during error-boundary recovery, lazy-load
    // race conditions, or Capacitor cold-start with no cached session).
    // Warn only in dev so production console stays clean; the stub itself is
    // safe regardless, and a dev-only warning still surfaces the misuse.
    if (import.meta.env.DEV && typeof console !== "undefined") {
      console.warn(
        "[useOrganization] Called outside OrganizationProvider — returning loading stub. " +
        "This is safe but may indicate a component rendered outside the authenticated route tree."
      );
    }
    return ORGANIZATION_LOADING_STUB;
  }
  return context;
}

export function useOptionalOrganization() {
  return useContext(OrganizationContext) ?? null;
}

// Helper hook for queries that need organization_id
export function useOrgId() {
  const { organizationId } = useOrganization();
  return organizationId;
}
